import { useEffect, useState } from "react";
import { optimizeUrl } from "@/lib/img";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PremiumBadge, LevelBadge } from "@/components/Badges";
import { ArrowLeft, Check, X, MapPin, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { labelTalentType } from "@/lib/roleCategories";

const STATUS_LABEL = {
  sent: "Nouvelle",
  viewed: "Vue",
  shortlisted: "Shortlistée",
  accepted: "Acceptée",
  refused: "Refusée",
};

const DEFAULT_REFUSAL = "Merci pour votre candidature et l'intérêt porté à notre projet. Cette fois-ci, nous avons retenu un autre profil. Nous restons attentifs aux prochaines opportunités.";

export default function ProCollabApplications() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [collab, setCollab] = useState(null);
  const [refuseTarget, setRefuseTarget] = useState(null); // { app_id, talent_name }
  const [refuseMessage, setRefuseMessage] = useState(DEFAULT_REFUSAL);
  const [refuseSubmitting, setRefuseSubmitting] = useState(false);

  useEffect(() => {
    load();
    api.get(`/collaborations/${id}`)
      .then(({ data }) => setCollab(data))
      .catch(() => setCollab(null));
  }, [id]);

  const load = async () => {
    try {
      const { data } = await api.get(`/collaborations/${id}/applications`);
      setApps(Array.isArray(data) ? data : []);
    } catch {
      setApps([]);
    }
  };

  const handleStatus = async (appId, status) => {
    try {
      await api.put(`/applications/${appId}`, { status });
      const labels = { accepted: "Candidature acceptée", shortlisted: "Candidature shortlistée" };
      toast.success(labels[status] || "Statut mis à jour");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la mise à jour");
    }
  };

  const openRefuse = (app) => {
    setRefuseTarget({ app_id: app.id, talent_name: app.talent?.name || "ce talent" });
    setRefuseMessage(DEFAULT_REFUSAL);
  };

  const submitRefuse = async () => {
    if (!refuseTarget) return;
    setRefuseSubmitting(true);
    try {
      await api.put(`/applications/${refuseTarget.app_id}`, {
        status: "refused",
        refusal_message: refuseMessage.trim() || undefined,
      });
      toast.success("Candidature refusée — message envoyé.");
      setRefuseTarget(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setRefuseSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div data-testid="pro-apps-page">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#666] hover:text-[#1A1A1A] mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>
        <p className="caption-label text-[#C68E59] mb-3">Candidatures reçues</p>
        <h1 className="font-serif text-4xl text-[#1A1A1A] mb-10">
          {collab?.title || "Collaboration"}
        </h1>

        {apps.length === 0 && (
          <div className="card-lux p-12 text-center text-[#666]">Aucune candidature pour l'instant.</div>
        )}

        <div className="space-y-6">
          {apps.map((a) => {
            const talent = a.talent;
            const profile = a.talent_profile;
            // Prefer snapshot photos (captured at application time) over live ones
            const photos = a.has_snapshot && a.snapshot_photos?.length
              ? a.snapshot_photos
              : (a.talent_photos || []);
            const main = photos.find((p) => p.is_main) || photos[0];
            const mainSrc = optimizeUrl(main ? `${API}/files/${main.storage_path}` : profile?.demo_photo_url, 480);
            const snapshotDate = a.snapshot_captured_at ? new Date(a.snapshot_captured_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : null;
            return (
              <div key={a.id} className="card-lux p-6 grid lg:grid-cols-5 gap-6" data-testid={`app-card-${a.id}`}>
                <div className="lg:col-span-2">
                  {mainSrc ? (
                    <img src={mainSrc} alt="" className="w-full aspect-[4/5] object-cover rounded-2xl" />
                  ) : (
                    <div className="w-full aspect-[4/5] bg-[#FAF3EA] rounded-2xl" />
                  )}
                  {photos.length > 1 && (
                    <div className="grid grid-cols-4 gap-2 mt-3">
                      {photos.slice(0, 4).map((p) => (
                        <img key={p.id} src={optimizeUrl(`${API}/files/${p.storage_path}`, 320)} alt="" loading="lazy" className="aspect-square object-cover rounded-lg" />
                      ))}
                    </div>
                  )}
                  {a.has_snapshot && snapshotDate && (
                    <p className="mt-3 text-[11px] tracking-widest uppercase text-[#8B5A2B] font-medium" data-testid={`snapshot-${a.id}`}>
                      Portfolio capturé le {snapshotDate}
                    </p>
                  )}
                </div>
                <div className="lg:col-span-3">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {profile && <LevelBadge level={profile.level} />}
                    {profile?.is_premium && <PremiumBadge />}
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#FAF3EA] text-[#666] border border-[#E5E5E5]">
                      {STATUS_LABEL[a.status]}
                    </span>
                  </div>
                  <Link to={`/talents/${talent?.user_id}`} className="font-serif text-3xl text-[#1A1A1A] hover:underline">
                    {talent?.name}
                  </Link>
                  <div className="flex items-center gap-4 mt-2 text-sm text-[#666]">
                    {profile?.talent_type && <span>{labelTalentType(profile.talent_type)}</span>}
                    {profile?.location && (
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{profile.location}</span>
                    )}
                  </div>
                  {a.message && (
                    <div className="mt-4 p-4 rounded-xl bg-[#F9F9F9] text-sm text-[#1A1A1A]">
                      « {a.message} »
                    </div>
                  )}
                  {profile?.bio && (
                    <p className="mt-4 text-sm text-[#666] line-clamp-3">{profile.bio}</p>
                  )}

                  <div className="flex gap-3 mt-6 flex-wrap">
                    <Button
                      onClick={() => handleStatus(a.id, "shortlisted")}
                      disabled={a.status === "shortlisted" || a.status === "accepted"}
                      variant="outline"
                      className="rounded-full border-[#C68E59] text-[#8B5A2B] hover:bg-[#FAF3EA]"
                      data-testid={`shortlist-${a.id}`}
                    >
                      <Star className="w-4 h-4 mr-2" /> Shortlister
                    </Button>
                    <Button
                      onClick={() => handleStatus(a.id, "accepted")}
                      disabled={a.status === "accepted"}
                      className="rounded-full bg-[#2E7D32] hover:bg-[#246428] text-white"
                      data-testid={`accept-${a.id}`}
                    >
                      <Check className="w-4 h-4 mr-2" /> Accepter
                    </Button>
                    <Button
                      onClick={() => openRefuse(a)}
                      disabled={a.status === "refused"}
                      variant="outline"
                      className="rounded-full border-[#A3483F] text-[#A3483F] hover:bg-[#FEEFEC]"
                      data-testid={`refuse-${a.id}`}
                    >
                      <X className="w-4 h-4 mr-2" /> Refuser
                    </Button>
                    <Link to={`/talents/${talent?.user_id}`}>
                      <Button variant="outline" className="rounded-full border-[#1A1A1A] text-[#1A1A1A]">
                        Voir le profil
                      </Button>
                    </Link>
                    {a.status === "accepted" && a.mission_id && (
                      <Link to={`/missions/${a.mission_id}`}>
                        <Button className="rounded-full bg-[#1A1A1A] hover:bg-[#2A1E12] text-white" data-testid={`open-mission-${a.id}`}>
                          Ouvrir la mission
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      </div>

      {/* Refuse modal with custom message */}
      <Dialog open={!!refuseTarget} onOpenChange={(o) => !o && setRefuseTarget(null)}>
        <DialogContent className="rounded-3xl" data-testid="refuse-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Refuser la candidature</DialogTitle>
            <DialogDescription className="text-[#6E4B2A]">
              Personnalisez votre message à {refuseTarget?.talent_name}. Un mot bienveillant compte beaucoup.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="caption-label">Message envoyé au talent</Label>
            <Textarea
              value={refuseMessage}
              onChange={(e) => setRefuseMessage(e.target.value)}
              rows={5}
              className="rounded-xl"
              data-testid="refuse-message-input"
            />
            <p className="text-xs text-[#8C7D6B]">Le talent recevra ce message dans ses notifications.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefuseTarget(null)} className="rounded-full">Annuler</Button>
            <Button
              onClick={submitRefuse}
              disabled={refuseSubmitting}
              className="rounded-full bg-[#A3483F] hover:bg-[#7A2F27] text-white"
              data-testid="refuse-submit"
            >
              {refuseSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer & refuser"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
